import { FieldErrors } from 'react-hook-form';
import { ui } from '@procuros/datachecks';
import get from 'lodash/get';

import { DisplayModes } from 'components/DataTable/Types';

import {
  getAllWebEDIErrors,
  interpolateErrorVariables,
  parseErrorMessage,
  trackItemModificationGroupsError,
} from './utils';
import { ErrorsNavigationService } from '../errorsNavigation/errorsNavigation';
import { scrollToWebEDIError } from './navigationHelpers';
import { remapErrorsToFirstCell } from './remapErrors';
import { sortArrayBasedOnConfig } from './sortErrors';
import { WebEDIError } from './types';

export class WebEDIErrorsService extends ErrorsNavigationService<WebEDIError> {
  constructor() {
    super();
  }

  focusOnCurrentError = async (): Promise<void> => {
    const currentError = this.getCurrentError();

    if (currentError) {
      return scrollToWebEDIError(currentError, this.scrollToVirtualizedRow);
    }
    return scrollToWebEDIError(this.store.getState().errors[0], this.scrollToVirtualizedRow);
  };

  setFieldsPendingValidation = (errors: Array<WebEDIError>, dirtyFields: Record<string, unknown>): void => {
    const pendingFieldIds = errors
      .filter((error) => Boolean(dirtyFields && get(dirtyFields, error.id)))
      .map((error) => error.id);

    this.store.getState().setFieldsPendingValidation(pendingFieldIds);
  };

  clearFieldsPendingValidation = (): void => {
    this.store.getState().clearFieldsPendingValidation();
  };

  processDocumentErrors = (
    formErrors: FieldErrors,
    variables: Record<string, string>,
    uiConfig: Array<ui.Section> | undefined,
    displayMode: DisplayModes,
    dirtyFields: Record<string, unknown>,
    isBigMessage: boolean,
  ): Array<WebEDIError> => {
    const webEDIErrors = getAllWebEDIErrors(formErrors, variables);

    if (displayMode === DisplayModes.view) {
      Object.values(webEDIErrors).forEach(trackItemModificationGroupsError);
    }

    if (!webEDIErrors || !uiConfig) {
      return [];
    }
    const { remappedErrors, hiddenErrors } = remapErrorsToFirstCell(webEDIErrors, uiConfig);
    const organizedErrors = sortArrayBasedOnConfig(Array.from(Object.values(remappedErrors)), uiConfig);
    if (isBigMessage && displayMode === DisplayModes.edit) {
      this.setFieldsPendingValidation(organizedErrors, dirtyFields);
    } else {
      this.clearFieldsPendingValidation();
    }
    this.setErrors(organizedErrors);

    // Also collect errors with type 'hidden' from form errors (datachecks)
    const hiddenDatachecksErrors: Array<WebEDIError> = [];
    const collectHiddenErrors = (obj: Record<string, unknown>, path = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const currentPath = path ? `${path}.${key}` : key;
        if (value && typeof value === 'object') {
          if ('type' in value && value.type === 'hidden' && 'message' in value) {
            // Parse the error message to extract title/description
            const error = parseErrorMessage(value.message);
            const { title, description } = interpolateErrorVariables(error, variables);
            hiddenDatachecksErrors.push({
              id: currentPath,
              title,
              description,
              type: 'hidden',
            });
          } else if (!Array.isArray(value)) {
            collectHiddenErrors(value as Record<string, unknown>, currentPath);
          }
        }
      });
    };
    collectHiddenErrors(formErrors as Record<string, unknown>);

    // Combine both sources of hidden errors
    return [...hiddenErrors, ...hiddenDatachecksErrors];
  };
}
